import { useMemo } from 'react';
import {
  computeEffectiveness,
  computeFrictionStartSummary,
  computeFrictionSubtypes,
  computeHeatmap,
  computeInterruptionSources,
  computePrepChecklistSummary,
  computeTinyWinSummary,
  computeTransitions
} from '../lib/insights';
import { rangeStartMs } from '../lib/time';
import type { DateRange, Ping, RescueSession } from '../types';

type UseInsightsDataParams = {
  pings: Ping[];
  rescues: RescueSession[];
  insightRange: DateRange;
};

export function useInsightsData({ pings, rescues, insightRange }: UseInsightsDataParams) {
  const rangedPings = useMemo(() => pings.filter((p) => p.ts_ms >= rangeStartMs(insightRange)), [insightRange, pings]);
  const rangedRescues = useMemo(() => rescues.filter((r) => r.ts_start_ms >= rangeStartMs(insightRange)), [insightRange, rescues]);
  const hasInsightsData = rangedPings.length > 0 || rangedRescues.length > 0;

  const heat = useMemo(() => computeHeatmap(rangedPings), [rangedPings]);
  const transitions = useMemo(() => computeTransitions(rangedPings), [rangedPings]);
  const effectiveness = useMemo(() => computeEffectiveness(rangedPings, rangedRescues), [rangedPings, rangedRescues]);
  const interruptions = useMemo(() => computeInterruptionSources(rangedPings), [rangedPings]);
  const subtypes = useMemo(() => computeFrictionSubtypes(rangedPings), [rangedPings]);
  const tinyWins = useMemo(() => computeTinyWinSummary(rangedRescues), [rangedRescues]);
  const prepSummary = useMemo(() => computePrepChecklistSummary(rangedRescues), [rangedRescues]);
  const frictionStart = useMemo(() => computeFrictionStartSummary(rangedPings), [rangedPings]);

  const todayStart = rangeStartMs('today');
  const todayPings = useMemo(() => pings.filter((p) => p.ts_ms >= todayStart), [pings, todayStart]);
  const todayRescuesCompleted = useMemo(
    () => rescues.filter((r) => r.ts_start_ms >= todayStart && r.completed === 1).length,
    [rescues, todayStart]
  );

  const todayAvgStuckMin = useMemo(() => {
    const leads = todayPings
      .map((p) => (p.friction_started_ms ? (p.ts_ms - p.friction_started_ms) / 60000 : null))
      .filter((x): x is number => x !== null && x > 0);
    if (leads.length === 0) return null;
    return leads.reduce((a, b) => a + b, 0) / leads.length;
  }, [todayPings]);

  return {
    rangedPings,
    rangedRescues,
    hasInsightsData,
    heat,
    transitions,
    effectiveness,
    interruptions,
    subtypes,
    tinyWins,
    prepSummary,
    frictionStart,
    todayPings,
    todayRescuesCompleted,
    todayAvgStuckMin
  };
}
